import { useState, useMemo, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed");
  return data;
};

const INR = (v) => "₹" + Math.round(Number(v)||0).toLocaleString("en-IN");

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00", bg:"#f0fdf4" },
  { id:"amazon",   name:"Amazon",   color:"#c45000", bg:"#fff3e0" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0", bg:"#eff6ff" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c", bg:"#fff0f5" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089", bg:"#fdf4ff" },
];

const TABS = [
  { id:"customers", label:"👥 Customers"    },
  { id:"rfm",       label:"📊 RFM Analysis" },
  { id:"returns",   label:"↩️ Returns"      },
  { id:"comms",     label:"💬 Communications"},
  { id:"insights",  label:"🤖 AI Insights"  },
];

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  th:     { padding:"7px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
  td:     { padding:"7px 12px", fontSize:12, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:  { padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  badge:  (c,bg) => ({ background:bg, color:c, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }),
};

// Build customer profiles from orders
function buildCustomers(orders) {
  const map = {};
  orders.forEach(o => {
    const phone = o.customer?.phone || o.shipping_address?.phone || "";
    const email = o.customer?.email || "";
    const key   = phone || email || (o.customer?.first_name||"")+(o.customer?.last_name||"");
    if (!key) return;
    const name  = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim() || "Guest";
    const city  = o.shipping_address?.city || "";
    const state = o.shipping_address?.province || "";
    const plat  = o._platform || "shopify";
    const amt   = parseFloat(o.total_price||0);
    const date  = new Date(o.created_at||0);

    if (!map[key]) {
      map[key] = { id:key, name, phone, email, city, state, platforms:new Set(), orders:[], totalSpend:0, firstOrder:date, lastOrder:date };
    }
    map[key].orders.push(o);
    map[key].totalSpend   += amt;
    map[key].platforms.add(plat);
    if (date < map[key].firstOrder) map[key].firstOrder = date;
    if (date > map[key].lastOrder)  map[key].lastOrder  = date;
  });

  return Object.values(map).map(c => ({
    ...c,
    platforms:    [...c.platforms],
    orderCount:   c.orders.length,
    avgOrderValue:c.orders.length ? c.totalSpend/c.orders.length : 0,
    daysSinceLast:Math.floor((Date.now()-c.lastOrder)/86400000),
  })).sort((a,b) => b.totalSpend - a.totalSpend);
}

// RFM scoring
function rfmScore(customer, allCustomers) {
  const maxSpend   = Math.max(...allCustomers.map(c=>c.totalSpend), 1);
  const maxOrders  = Math.max(...allCustomers.map(c=>c.orderCount), 1);

  const R = customer.daysSinceLast <= 30  ? 5 : customer.daysSinceLast <= 60  ? 4 : customer.daysSinceLast <= 90  ? 3 : customer.daysSinceLast <= 180 ? 2 : 1;
  const F = customer.orderCount >= 10     ? 5 : customer.orderCount >= 7      ? 4 : customer.orderCount >= 4      ? 3 : customer.orderCount >= 2      ? 2 : 1;
  const M = customer.totalSpend/maxSpend >= 0.8 ? 5 : customer.totalSpend/maxSpend >= 0.5 ? 4 : customer.totalSpend/maxSpend >= 0.3 ? 3 : customer.totalSpend/maxSpend >= 0.1 ? 2 : 1;

  const score    = (R + F + M) / 3;
  const segment  = score >= 4.5 ? "Champions" : score >= 3.5 ? "Loyal" : score >= 2.5 ? "Potential" : score >= 1.5 ? "At Risk" : "Dormant";
  const segColor = { Champions:"#15803d", Loyal:"#2563eb", Potential:"#d97706", "At Risk":"#dc2626", Dormant:"#9ca3af" };
  const segBg    = { Champions:"#f0fdf4", Loyal:"#eff6ff", Potential:"#fffbeb", "At Risk":"#fef2f2", Dormant:"#f9fafb" };

  return { R, F, M, score:Math.round(score*10)/10, segment, color:segColor[segment], bg:segBg[segment] };
}

export default function PageCustomerPro({ toast, orders=[], products=[], tenant }) {
  const [tab,       setTab]      = useState("customers");
  const [search,    setSearch]   = useState("");
  const [sortBy,    setSortBy]   = useState("spend");
  const [selCust,   setSelCust]  = useState(null);
  const [filterSeg, setFilterSeg]= useState("all");
  const [returns,   setReturns]  = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_returns")||"[]"); } catch { return []; }
  });
  const [retForm,   setRetForm]  = useState({ orderId:"", customer:"", product:"", reason:"", platform:"shopify", amount:"", date:new Date().toISOString().slice(0,10) });
  const [showRetForm,setShowRetForm] = useState(false);
  const [comms,     setComms]    = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_comms")||"[]"); } catch { return []; }
  });
  const [commForm,  setCommForm] = useState({ customer:"", type:"WhatsApp", note:"", date:new Date().toISOString().slice(0,10) });
  const [showCommForm,setShowCommForm] = useState(false);
  const [aiLoading, setAiLoading]= useState(false);
  const [aiResult,  setAiResult] = useState(null);

  // Build customers from orders
  const customers = useMemo(() => buildCustomers(orders), [orders]);

  // RFM for each customer
  const customersWithRFM = useMemo(() =>
    customers.map(c => ({ ...c, rfm: rfmScore(c, customers) })),
  [customers]);

  // Filtered + sorted
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return customersWithRFM.filter(c => {
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.phone.includes(q) || c.city.toLowerCase().includes(q);
      const matchSeg    = filterSeg==="all" || c.rfm.segment===filterSeg;
      return matchSearch && matchSeg;
    }).sort((a,b) => {
      if (sortBy==="spend")  return b.totalSpend - a.totalSpend;
      if (sortBy==="orders") return b.orderCount - a.orderCount;
      if (sortBy==="recent") return a.daysSinceLast - b.daysSinceLast;
      if (sortBy==="name")   return a.name.localeCompare(b.name);
      return 0;
    });
  }, [customersWithRFM, search, filterSeg, sortBy]);

  // Stats
  const totalCustomers  = customers.length;
  const repeatCustomers = customers.filter(c=>c.orderCount>1).length;
  const totalRevenue    = customers.reduce((a,c)=>a+c.totalSpend,0);
  const avgOrderValue   = orders.length ? totalRevenue/orders.length : 0;
  const segments        = {};
  customersWithRFM.forEach(c=>{ segments[c.rfm.segment]=(segments[c.rfm.segment]||0)+1; });

  // Add return
  const addReturn = () => {
    if (!retForm.orderId||!retForm.customer) { toast("Order ID and customer required","error"); return; }
    const updated = [{ ...retForm, id:Date.now() }, ...returns];
    setReturns(updated);
    localStorage.setItem("3june_returns", JSON.stringify(updated));
    setRetForm({ orderId:"", customer:"", product:"", reason:"", platform:"shopify", amount:"", date:new Date().toISOString().slice(0,10) });
    setShowRetForm(false);
    toast("Return logged ✓","success");
  };

  const deleteReturn = (id) => {
    const updated = returns.filter(r=>r.id!==id);
    setReturns(updated); localStorage.setItem("3june_returns", JSON.stringify(updated));
  };

  // Add communication
  const addComm = () => {
    if (!commForm.customer) { toast("Customer name required","error"); return; }
    const updated = [{ ...commForm, id:Date.now() }, ...comms];
    setComms(updated);
    localStorage.setItem("3june_comms", JSON.stringify(updated));
    setCommForm({ customer:"", type:"WhatsApp", note:"", date:new Date().toISOString().slice(0,10) });
    setShowCommForm(false);
    toast("Communication logged ✓","success");
  };

  // AI Insights
  const runAI = async () => {
    setAiLoading(true);
    try {
      const champions = customersWithRFM.filter(c=>c.rfm.segment==="Champions").length;
      const atRisk    = customersWithRFM.filter(c=>c.rfm.segment==="At Risk").length;
      const dormant   = customersWithRFM.filter(c=>c.rfm.segment==="Dormant").length;
      const data = await authFetch("/ai/text", {
        method:"POST",
        body: JSON.stringify({
          prompt: `You are a customer analytics expert for an Indian ecommerce leather belts brand.

Customer Data:
- Total customers: ${totalCustomers}
- Repeat customers: ${repeatCustomers} (${totalCustomers>0?Math.round(repeatCustomers/totalCustomers*100):0}%)
- Champions (best customers): ${champions}
- At Risk customers: ${atRisk}
- Dormant customers: ${dormant}
- Average order value: Rs.${Math.round(avgOrderValue)}
- Total revenue: Rs.${Math.round(totalRevenue).toLocaleString("en-IN")}
- Returns logged: ${returns.length}

Provide in JSON:
{
  "keyInsights": ["insight1","insight2","insight3"],
  "retentionTips": ["tip1","tip2","tip3"],
  "atRiskStrategy": "What to do for at-risk customers",
  "growthOpportunity": "One key growth opportunity"
}
Return ONLY valid JSON.`,
          maxTokens:600,
        }),
      });
      const text = (data.result||"").replace(/```json|```/g,"").trim();
      setAiResult(JSON.parse(text));
      toast("AI insights ready ✓","success");
    } catch(e) { toast("AI error: "+e.message,"error"); }
    setAiLoading(false);
  };

  const exportCustomers = () => {
    const headers = ["Name","Phone","Email","City","State","Platforms","Orders","Total Spend","Avg Order","Last Order (days ago)","RFM Segment","RFM Score"];
    const rows    = filtered.map(c=>[c.name,c.phone,c.email,c.city,c.state,c.platforms.join("|"),c.orderCount,Math.round(c.totalSpend),Math.round(c.avgOrderValue),c.daysSinceLast,c.rfm.segment,c.rfm.score]);
    const csv     = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a       = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="customers.csv"; a.click();
    toast("Customers exported ✓","success");
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>👥 Customer Management</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Customer profiles, RFM analysis, returns and communications</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={S.btnOut} onClick={exportCustomers}>⬇ Export CSV</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="kpi-grid" className="rfm-segment-grid" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"Total Customers",  value:totalCustomers,                                                  color:"#2563eb", icon:"👥" },
          { label:"Repeat Buyers",    value:repeatCustomers,                                                  color:"#15803d", icon:"🔄" },
          { label:"Repeat Rate",      value:totalCustomers>0?Math.round(repeatCustomers/totalCustomers*100)+"%":"0%", color:"#F47920", icon:"📈" },
          { label:"Avg Order Value",  value:INR(avgOrderValue),                                              color:"#7e22ce", icon:"💰" },
          { label:"Total Revenue",    value:INR(totalRevenue),                                               color:"#15803d", icon:"💎" },
        ].map(k=>(
          <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"11px 14px", borderLeft:"3px solid "+k.color, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{k.icon}</span>
            <div>
              <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
              <div style={{ fontSize:17, fontWeight:700, color:k.color }}>{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14, overflowX:"auto" }}>
        {TABS.map(t=>(
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", background:"none", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ CUSTOMERS ══ */}
      {tab === "customers" && (
        <div>
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...S.input, width:220 }} placeholder="🔍 Search name, phone, city…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ ...S.input, width:"auto" }} value={filterSeg} onChange={e=>setFilterSeg(e.target.value)}>
              <option value="all">All Segments</option>
              {["Champions","Loyal","Potential","At Risk","Dormant"].map(s=><option key={s}>{s}</option>)}
            </select>
            <select style={{ ...S.input, width:"auto" }} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              <option value="spend">Sort: Total Spend</option>
              <option value="orders">Sort: Orders</option>
              <option value="recent">Sort: Most Recent</option>
              <option value="name">Sort: Name</option>
            </select>
            <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length} customers</span>
          </div>

          {customers.length===0 ? (
            <div style={{ ...S.card, padding:60, textAlign:"center", color:"#9ca3af" }}>
              <div style={{ fontSize:40, marginBottom:10 }}>👥</div>
              <div>Customer profiles build automatically from your orders</div>
              <div style={{ fontSize:11, marginTop:6 }}>Sync your orders to see customer profiles here</div>
            </div>
          ) : (
            <div style={{ ...S.card, overflow:"hidden" }}>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>{["Customer","Location","Platforms","Orders","Total Spend","Avg Order","Last Order","RFM","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {filtered.map((c,i)=>(
                      <tr key={c.id} style={{ background:i%2?"#fafafa":"#fff" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fff8f2"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2?"#fafafa":"#fff"}>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:32, height:32, borderRadius:"50%", background:"#F47920"+"22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#F47920", flexShrink:0 }}>
                              {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight:600, fontSize:12 }}>{c.name}</div>
                              <div style={{ fontSize:10, color:"#9ca3af" }}>{c.phone||c.email||"—"}</div>
                            </div>
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ fontSize:12 }}>{c.city||"—"}</div>
                          <div style={{ fontSize:10, color:"#9ca3af" }}>{c.state||""}</div>
                        </td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                            {c.platforms.map(p=>{ const pl=PLATFORMS.find(x=>x.id===p); return pl?<span key={p} style={{ fontSize:9, padding:"1px 5px", borderRadius:8, background:pl.bg, color:pl.color, fontWeight:600 }}>{pl.name}</span>:null; })}
                          </div>
                        </td>
                        <td style={{ ...S.td, fontWeight:700, textAlign:"center", color:"#2563eb" }}>{c.orderCount}</td>
                        <td style={{ ...S.td, fontWeight:700, color:"#15803d" }}>{INR(c.totalSpend)}</td>
                        <td style={S.td}>{INR(c.avgOrderValue)}</td>
                        <td style={{ ...S.td, color:c.daysSinceLast>90?"#dc2626":c.daysSinceLast>30?"#d97706":"#15803d", fontSize:11 }}>
                          {c.daysSinceLast===0?"Today":c.daysSinceLast===1?"Yesterday":c.daysSinceLast+" days ago"}
                        </td>
                        <td style={S.td}>
                          <span style={{ ...S.badge(c.rfm.color, c.rfm.bg), display:"inline-block" }}>{c.rfm.segment}</span>
                          <div style={{ fontSize:9, color:"#9ca3af", marginTop:2 }}>R{c.rfm.R} F{c.rfm.F} M{c.rfm.M}</div>
                        </td>
                        <td style={S.td}>
                          <button style={{ ...S.btn("#2563eb"), padding:"3px 10px", fontSize:10 }} onClick={()=>setSelCust(selCust?.id===c.id?null:c)}>
                            {selCust?.id===c.id?"Close":"View"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Customer detail panel */}
          {selCust && (
            <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
              <div style={S.hdr}>
                <span>👤 {selCust.name} — Order History</span>
                <button style={S.btnOut} onClick={()=>setSelCust(null)}>Close ×</button>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:0 }}>
                {/* Profile */}
                <div style={{ padding:16, borderRight:"1px solid #e5e7eb" }}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:"#F47920"+"22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:700, color:"#F47920", marginBottom:12 }}>
                    {selCust.name.charAt(0).toUpperCase()}
                  </div>
                  {[
                    ["Phone",   selCust.phone||"—"],
                    ["Email",   selCust.email||"—"],
                    ["City",    selCust.city||"—"],
                    ["State",   selCust.state||"—"],
                    ["Orders",  selCust.orderCount],
                    ["Total",   INR(selCust.totalSpend)],
                    ["Avg",     INR(selCust.avgOrderValue)],
                    ["Segment", selCust.rfm.segment],
                  ].map(([k,v])=>(
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #f9fafb", fontSize:12 }}>
                      <span style={{ color:"#9ca3af", fontWeight:600 }}>{k}</span>
                      <span style={{ fontWeight:500 }}>{v}</span>
                    </div>
                  ))}
                  {selCust.phone && (
                    <a href={"https://wa.me/91"+selCust.phone.replace(/\D/g,"")} target="_blank" rel="noreferrer"
                      style={{ ...S.btn("#25d366"), display:"block", textAlign:"center", marginTop:12, textDecoration:"none", fontSize:11 }}>
                      💬 WhatsApp
                    </a>
                  )}
                </div>
                {/* Orders */}
                <div style={{ overflowY:"auto", maxHeight:300 }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["Order","Date","Platform","Amount","Status"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {selCust.orders.sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map((o,i)=>(
                        <tr key={o.id||o.name} style={{ background:i%2?"#fafafa":"#fff" }}>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{o.name||o.id}</td>
                          <td style={{ ...S.td, fontSize:11 }}>{new Date(o.created_at||0).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"2-digit"})}</td>
                          <td style={S.td}>{(() => { const pl=PLATFORMS.find(p=>p.id===(o._platform||"shopify")); return pl?<span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:pl.bg, color:pl.color, fontWeight:600 }}>{pl.name}</span>:null; })()}</td>
                          <td style={{ ...S.td, fontWeight:600 }}>{INR(o.total_price||0)}</td>
                          <td style={S.td}><span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:o.fulfillment_status==="fulfilled"?"#f0fdf4":"#fff8f2", color:o.fulfillment_status==="fulfilled"?"#15803d":"#F47920", fontWeight:700 }}>{o.fulfillment_status||"Pending"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ RFM ANALYSIS ══ */}
      {tab === "rfm" && (
        <div>
          {/* Segment overview */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
            {[
              { seg:"Champions", color:"#15803d", bg:"#f0fdf4", icon:"🏆", desc:"Best customers. Bought recently, buy often, spend most." },
              { seg:"Loyal",     color:"#2563eb", bg:"#eff6ff", icon:"💙", desc:"Regular buyers. High frequency and monetary value." },
              { seg:"Potential", color:"#d97706", bg:"#fffbeb", icon:"⭐", desc:"Recent buyers with average frequency." },
              { seg:"At Risk",   color:"#dc2626", bg:"#fef2f2", icon:"⚠️", desc:"Used to buy but haven't recently. Need re-engagement." },
              { seg:"Dormant",   color:"#9ca3af", bg:"#f9fafb", icon:"💤", desc:"Haven't bought in a long time. Hard to win back." },
            ].map(s=>(
              <div key={s.seg} style={{ background:s.bg, borderRadius:10, border:"1px solid "+s.color+"33", padding:"12px 14px" }}>
                <div style={{ fontSize:20, marginBottom:6 }}>{s.icon}</div>
                <div style={{ fontWeight:700, fontSize:13, color:s.color }}>{s.seg}</div>
                <div style={{ fontSize:22, fontWeight:700, color:s.color, margin:"4px 0" }}>{segments[s.seg]||0}</div>
                <div style={{ fontSize:10, color:s.color, opacity:0.8, lineHeight:1.4 }}>{s.desc}</div>
              </div>
            ))}
          </div>

          {/* RFM Table */}
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}>
              <span>📊 RFM Scoring — All Customers</span>
              <div style={{ fontSize:11, color:"#9ca3af" }}>R=Recency F=Frequency M=Monetary (1-5 scale)</div>
            </div>
            <div style={{ overflowX:"auto", maxHeight:450, overflowY:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead style={{ position:"sticky", top:0 }}>
                  <tr>{["Customer","City","Orders","Total Spend","Last Purchase","R","F","M","Score","Segment","Action"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {customersWithRFM.sort((a,b)=>b.rfm.score-a.rfm.score).map((c,i)=>(
                    <tr key={c.id} style={{ background:i%2?"#fafafa":"#fff" }}>
                      <td style={S.td}>
                        <div style={{ fontWeight:600, fontSize:12 }}>{c.name}</div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{c.phone||"—"}</div>
                      </td>
                      <td style={{ ...S.td, fontSize:11 }}>{c.city||"—"}</td>
                      <td style={{ ...S.td, textAlign:"center", fontWeight:600, color:"#2563eb" }}>{c.orderCount}</td>
                      <td style={{ ...S.td, fontWeight:600, color:"#15803d" }}>{INR(c.totalSpend)}</td>
                      <td style={{ ...S.td, fontSize:11, color:c.daysSinceLast>90?"#dc2626":"#374151" }}>
                        {c.daysSinceLast===0?"Today":c.daysSinceLast+" days ago"}
                      </td>
                      {[c.rfm.R, c.rfm.F, c.rfm.M].map((score,j)=>(
                        <td key={j} style={{ ...S.td, textAlign:"center" }}>
                          <span style={{ display:"inline-block", width:22, height:22, borderRadius:"50%", background:score>=4?"#15803d":score>=3?"#d97706":"#9ca3af", color:"#fff", fontSize:11, fontWeight:700, lineHeight:"22px", textAlign:"center" }}>{score}</span>
                        </td>
                      ))}
                      <td style={{ ...S.td, textAlign:"center", fontWeight:700, color:"#F47920" }}>{c.rfm.score}</td>
                      <td style={S.td}><span style={{ ...S.badge(c.rfm.color, c.rfm.bg), display:"inline-block" }}>{c.rfm.segment}</span></td>
                      <td style={S.td}>
                        {c.rfm.segment==="At Risk" && c.phone && (
                          <a href={"https://wa.me/91"+c.phone.replace(/\D/g,"")} target="_blank" rel="noreferrer" style={{ ...S.btn("#25d366"), textDecoration:"none", padding:"3px 8px", fontSize:10 }}>Win Back</a>
                        )}
                        {c.rfm.segment==="Champions" && c.phone && (
                          <a href={"https://wa.me/91"+c.phone.replace(/\D/g,"")} target="_blank" rel="noreferrer" style={{ ...S.btn("#F47920"), textDecoration:"none", padding:"3px 8px", fontSize:10 }}>Reward</a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ RETURNS ══ */}
      {tab === "returns" && (
        <div>
          {showRetForm && (
            <div style={{ ...S.card, padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>➕ Log Return</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                <div>
                  <label style={S.label}>Order ID *</label>
                  <input style={{ ...S.input, width:"100%" }} placeholder="#1001" value={retForm.orderId} onChange={e=>setRetForm(f=>({...f,orderId:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Customer *</label>
                  <input style={{ ...S.input, width:"100%" }} placeholder="Customer name" value={retForm.customer} onChange={e=>setRetForm(f=>({...f,customer:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Platform</label>
                  <select style={{ ...S.input, width:"100%" }} value={retForm.platform} onChange={e=>setRetForm(f=>({...f,platform:e.target.value}))}>
                    {PLATFORMS.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Product</label>
                  <input style={{ ...S.input, width:"100%" }} placeholder="Product name or SKU" value={retForm.product} onChange={e=>setRetForm(f=>({...f,product:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Return Amount ₹</label>
                  <input type="number" style={{ ...S.input, width:"100%" }} placeholder="0" value={retForm.amount} onChange={e=>setRetForm(f=>({...f,amount:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Date</label>
                  <input type="date" style={{ ...S.input, width:"100%" }} value={retForm.date} onChange={e=>setRetForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Return Reason</label>
                  <select style={{ ...S.input, width:"100%" }} value={retForm.reason} onChange={e=>setRetForm(f=>({...f,reason:e.target.value}))}>
                    <option value="">Select reason…</option>
                    {["Size issue","Quality issue","Wrong product","Damaged in transit","Customer changed mind","Defective product","Not as described"].map(r=><option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={S.btn()} onClick={addReturn}>Save Return</button>
                <button style={S.btnOut} onClick={()=>setShowRetForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Returns KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Total Returns",  value:returns.length,                                                         color:"#dc2626" },
              { label:"Return Amount",  value:INR(returns.reduce((a,r)=>a+(parseFloat(r.amount)||0),0)),              color:"#7e22ce" },
              { label:"Return Rate",    value:orders.length>0?Math.round(returns.length/orders.length*100)+"%":"0%",  color:"#d97706" },
              { label:"Top Reason",     value:(() => { const rc={}; returns.forEach(r=>{ rc[r.reason]=(rc[r.reason]||0)+1; }); return Object.entries(rc).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—"; })(), color:"#6b7280" },
            ].map(k=>(
              <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
                <div style={{ fontSize:16, fontWeight:700, color:k.color, marginTop:3 }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
            <button style={S.btn()} onClick={()=>setShowRetForm(true)}>+ Log Return</button>
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}><span>↩️ Return Log</span></div>
            {returns.length===0 ? (
              <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>No returns logged yet</div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Date","Order ID","Customer","Platform","Product","Reason","Amount",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {returns.map((r,i)=>{
                    const pl = PLATFORMS.find(p=>p.id===r.platform);
                    return (
                      <tr key={r.id} style={{ background:i%2?"#fafafa":"#fff" }}>
                        <td style={{ ...S.td, fontSize:11 }}>{r.date}</td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:10, color:"#F47920" }}>{r.orderId}</td>
                        <td style={S.td}>{r.customer}</td>
                        <td style={S.td}>{pl?<span style={{ ...S.badge(pl.color,pl.bg), fontSize:9 }}>{pl.name}</span>:r.platform}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{r.product||"—"}</td>
                        <td style={S.td}><span style={{ fontSize:10, background:"#fef2f2", color:"#dc2626", padding:"2px 7px", borderRadius:8 }}>{r.reason||"—"}</span></td>
                        <td style={{ ...S.td, fontWeight:600, color:"#dc2626" }}>{r.amount?INR(r.amount):"—"}</td>
                        <td style={S.td}><button style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:14 }} onClick={()=>deleteReturn(r.id)}>×</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ══ COMMUNICATIONS ══ */}
      {tab === "comms" && (
        <div>
          {showCommForm && (
            <div style={{ ...S.card, padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>➕ Log Communication</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                <div>
                  <label style={S.label}>Customer *</label>
                  <input style={{ ...S.input, width:"100%" }} placeholder="Customer name" value={commForm.customer} onChange={e=>setCommForm(f=>({...f,customer:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Channel</label>
                  <select style={{ ...S.input, width:"100%" }} value={commForm.type} onChange={e=>setCommForm(f=>({...f,type:e.target.value}))}>
                    {["WhatsApp","Phone Call","Email","Instagram DM","Platform Message"].map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Date</label>
                  <input type="date" style={{ ...S.input, width:"100%" }} value={commForm.date} onChange={e=>setCommForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Note</label>
                  <textarea style={{ ...S.input, width:"100%", height:70, resize:"vertical" }} placeholder="What was discussed…" value={commForm.note} onChange={e=>setCommForm(f=>({...f,note:e.target.value}))}/>
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={S.btn()} onClick={addComm}>Save</button>
                <button style={S.btnOut} onClick={()=>setShowCommForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <div style={{ fontSize:12, color:"#9ca3af" }}>{comms.length} communication logs</div>
            <button style={S.btn()} onClick={()=>setShowCommForm(true)}>+ Log Communication</button>
          </div>

          <div style={S.card}>
            {comms.length===0 ? (
              <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>💬</div>
                <div>No communications logged yet</div>
                <div style={{ fontSize:11, marginTop:6 }}>Log every customer call, WhatsApp chat or email here</div>
              </div>
            ) : comms.map((c,i)=>{
              const typeColors = { "WhatsApp":"#25d366", "Phone Call":"#2563eb", "Email":"#d97706", "Instagram DM":"#E1306C", "Platform Message":"#F47920" };
              return (
                <div key={c.id} style={{ padding:"12px 16px", borderBottom:"1px solid #f3f4f6", display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:(typeColors[c.type]||"#9ca3af")+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>
                    {c.type==="WhatsApp"?"💬":c.type==="Phone Call"?"📞":c.type==="Email"?"📧":c.type==="Instagram DM"?"📸":"💬"}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontWeight:600, fontSize:12 }}>{c.customer}</span>
                      <span style={{ fontSize:10, color:"#9ca3af" }}>{c.date}</span>
                    </div>
                    <div style={{ marginBottom:4 }}>
                      <span style={{ fontSize:10, padding:"1px 7px", borderRadius:8, background:(typeColors[c.type]||"#9ca3af")+"18", color:typeColors[c.type]||"#9ca3af", fontWeight:600 }}>{c.type}</span>
                    </div>
                    {c.note && <div style={{ fontSize:12, color:"#6b7280", lineHeight:1.5 }}>{c.note}</div>}
                  </div>
                  <button style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:14, flexShrink:0 }} onClick={()=>{ const u=comms.filter(x=>x.id!==c.id); setComms(u); localStorage.setItem("3june_comms",JSON.stringify(u)); }}>×</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ AI INSIGHTS ══ */}
      {tab === "insights" && (
        <div>
          <div style={{ ...S.card, padding:16, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>🤖 AI Customer Insights</div>
                <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>AI analyzes your customer data to suggest retention and growth strategies</div>
              </div>
              <button style={{ ...S.btn("#6366f1"), opacity:aiLoading?0.7:1 }} onClick={runAI} disabled={aiLoading}>
                {aiLoading?"🤖 Analyzing…":"🤖 Generate Insights"}
              </button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, background:"#f9fafb", borderRadius:8, padding:12 }}>
              {[
                { label:"Analyzing", value:totalCustomers+" customers" },
                { label:"Segments",  value:Object.keys(segments).length+" found" },
                { label:"Returns",   value:returns.length+" logged" },
              ].map(k=>(
                <div key={k.label} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600 }}>{k.label}</div>
                  <div style={{ fontWeight:600, fontSize:13, color:"#374151" }}>{k.value}</div>
                </div>
              ))}
            </div>
          </div>

          {aiResult && (
            <div>
              <div style={S.card}>
                <div style={S.hdr}><span>💡 Key Insights</span></div>
                <div style={{ padding:14 }}>
                  {(aiResult.keyInsights||[]).map((ins,i)=>(
                    <div key={i} style={{ display:"flex", gap:10, marginBottom:10, padding:"10px 12px", background:"#f9fafb", borderRadius:8 }}>
                      <span style={{ color:"#F47920", fontWeight:700, flexShrink:0 }}>{i+1}.</span>
                      <span style={{ fontSize:13, color:"#374151" }}>{ins}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="two-col-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div style={S.card}>
                  <div style={S.hdr}><span>🔄 Retention Tips</span></div>
                  <div style={{ padding:14 }}>
                    {(aiResult.retentionTips||[]).map((tip,i)=>(
                      <div key={i} style={{ display:"flex", gap:8, marginBottom:8, fontSize:12 }}>
                        <span style={{ color:"#15803d", fontWeight:700, flexShrink:0 }}>✓</span>
                        <span style={{ color:"#374151" }}>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={S.card}>
                    <div style={S.hdr}><span>⚠️ At Risk Strategy</span></div>
                    <div style={{ padding:14, fontSize:13, color:"#374151", lineHeight:1.6 }}>{aiResult.atRiskStrategy}</div>
                  </div>
                  <div style={S.card}>
                    <div style={S.hdr}><span>🚀 Growth Opportunity</span></div>
                    <div style={{ padding:14, fontSize:13, color:"#374151", lineHeight:1.6 }}>{aiResult.growthOpportunity}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
